import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  MapPin,
  Search,
  Plus,
  RefreshCw,
  Loader2,
  MoreHorizontal,
  Edit,
  Trash2,
} from 'lucide-react'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'
import { WorkRegion, WorkRegionState } from '../../model/WorkRegion'
import RegionEndpoints from '../../endpoints/RegionEndpoints'
import { toast } from 'sonner'

export const RegionList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')

  const { regions, refreshRegions, isLoading } = useApplicationStore()
  const { hasAdmin } = useSessionStore()

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRegion, setEditingRegion] = useState<WorkRegion | null>(null)
  const [regionName, setRegionName] = useState('')
  const [nameError, setNameError] = useState('')
  const [saving, setSaving] = useState(false)

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [regionToDelete, setRegionToDelete] = useState<WorkRegion | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    refreshRegions()
  }, [refreshRegions])

  const filteredRegions = regions
    .filter((region) =>
      searchTerm === '' || region.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name))

  const openCreate = () => {
    setEditingRegion(null)
    setRegionName('')
    setNameError('')
    setDialogOpen(true)
  }

  const openEdit = (region: WorkRegion) => {
    setEditingRegion(region)
    setRegionName(region.name)
    setNameError('')
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!regionName.trim()) {
      setNameError('Region name is required')
      return
    }

    setSaving(true)
    try {
      if (editingRegion) {
        await RegionEndpoints.editRegion(editingRegion.id, { name: regionName.trim() })
        toast.success('Region updated')
      } else {
        await RegionEndpoints.createRegion({ name: regionName.trim() })
        toast.success('Region created')
      }
      await refreshRegions()
      setDialogOpen(false)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save region'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!regionToDelete) return
    setDeleting(true)
    try {
      await RegionEndpoints.deleteRegion(regionToDelete.id)
      await refreshRegions()
      toast.success('Region deleted')
      setDeleteDialogOpen(false)
      setRegionToDelete(null)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete region'
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  const isAdmin = hasAdmin()

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Work Regions</h1>
              <p className="text-sm text-muted-foreground">
                {regions.length} region{regions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refreshRegions()} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New Region
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search regions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading() ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Loading regions...</p>
            </div>
          ) : filteredRegions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <MapPin className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-4 text-sm font-medium">No regions found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first region'}
              </p>
              {!searchTerm && isAdmin && (
                <Button className="mt-4" onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Region
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRegions.map((region) => (
                  <TableRow key={region.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                          {region.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium">{region.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="secondary"
                        className={region.state === WorkRegionState.REFERENCED
                          ? 'text-xs bg-green-50 text-green-700 hover:bg-green-50'
                          : 'text-xs'
                        }
                      >
                        {region.state === WorkRegionState.REFERENCED ? 'In Use' : 'Unused'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(region)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {region.state !== 'REFERENCED' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => { setRegionToDelete(region); setDeleteDialogOpen(true) }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open && !saving) setDialogOpen(false) }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editingRegion ? 'Edit Region' : 'Create Region'}</DialogTitle>
            <DialogDescription>
              {editingRegion ? 'Update the region name.' : 'Add a new work region.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="regionName">Name *</Label>
            <Input
              id="regionName"
              value={regionName}
              onChange={(e) => { setRegionName(e.target.value); if (nameError) setNameError('') }}
              placeholder="e.g. Ontario, Western Canada"
              className={nameError ? 'border-red-500' : ''}
              disabled={saving}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
              autoFocus
            />
            {nameError && <p className="text-sm text-red-500">{nameError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingRegion ? 'Save Changes' : 'Create Region'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open && !deleting) { setDeleteDialogOpen(false); setRegionToDelete(null) } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Region</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{regionToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
